import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Plus, Quote as QuoteIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  CollectionEmpty,
  CollectionList,
  CollectionLoading,
  CollectionPage,
  CollectionRow,
  FilterInput,
} from "@/components/layout/CollectionPage";
import { get, post } from "@/lib/api";
import { stripMarkdown } from "@/lib/markdown";
import { useToast } from "@/hooks/use-toast";
import type { Quote } from "@/types";

/** The quote itself is the headline; attribution is context beneath it. */
function quoteHeadline(quote: Quote) {
  const [firstLine] = stripMarkdown(quote.content).trim().split("\n");
  return firstLine?.trim() || "Empty quote";
}

function quoteAttribution(quote: Quote) {
  return [quote.author, quote.source].filter(Boolean).join(" · ") || "Unattributed";
}

function AddQuoteDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState({ content: "", author: "", source: "" });
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const addQuote = useMutation({
    mutationFn: () =>
      post("/quotes", {
        content: draft.content,
        author: draft.author,
        source: draft.source || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      setDraft({ content: "", author: "", source: "" });
      setIsOpen(false);
      toast({ title: "Quote saved" });
    },
    onError: () => toast({ title: "Couldn't save that quote", variant: "destructive" }),
  });

  const canSave = draft.content.trim().length > 0 && draft.author.trim().length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add quote
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add quote</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            placeholder="The quote itself…"
            value={draft.content}
            onChange={(event) => setDraft({ ...draft, content: event.target.value })}
          />
          <Input
            placeholder="Author"
            value={draft.author}
            onChange={(event) => setDraft({ ...draft, author: event.target.value })}
          />
          <Input
            placeholder="Source (optional)"
            value={draft.source}
            onChange={(event) => setDraft({ ...draft, source: event.target.value })}
          />
          <Button
            onClick={() => addQuote.mutate()}
            disabled={!canSave || addQuote.isPending}
            className="w-full"
          >
            {addQuote.isPending ? "Saving…" : "Save quote"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function QuotesPage() {
  const [filterText, setFilterText] = useState("");

  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ["/api/quotes"],
    queryFn: () => get<Quote[]>("/quotes"),
  });

  const visible = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    return quotes
      .filter(
        (quote) =>
          !needle ||
          quote.content.toLowerCase().includes(needle) ||
          quote.author?.toLowerCase().includes(needle) ||
          quote.source?.toLowerCase().includes(needle),
      )
      .sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [quotes, filterText]);

  const hasFilters = filterText.trim().length > 0;

  return (
    <CollectionPage
      title="Quotes"
      count={visible.length}
      noun="quote"
      isLoading={isLoading}
      action={<AddQuoteDialog />}
      filters={
        <>
          <FilterInput
            value={filterText}
            onChange={setFilterText}
            placeholder="Filter quotes…"
          />
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-muted-foreground"
              onClick={() => setFilterText("")}
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </>
      }
    >
      {isLoading ? (
        <CollectionLoading label="Loading quotes…" />
      ) : visible.length === 0 ? (
        <CollectionEmpty
          icon={<QuoteIcon className="h-10 w-10 text-muted-foreground" />}
          title={hasFilters ? "Nothing matches that filter." : "No quotes yet."}
          hint={
            hasFilters
              ? "Try a different word."
              : "Keep the lines worth reusing in one place."
          }
        />
      ) : (
        <CollectionList>
          {visible.map((quote) => (
            <CollectionRow
              key={quote.id}
              href={`/quotes/${quote.id}`}
              icon={<QuoteIcon className="h-4 w-4 text-muted-foreground" />}
              title={quoteHeadline(quote)}
              subtitle={quoteAttribution(quote)}
              meta={formatDistanceToNow(new Date(quote.created_at), { addSuffix: true })}
            />
          ))}
        </CollectionList>
      )}
    </CollectionPage>
  );
}
